import { Button, Tooltip } from "@design";
import { useQuery } from "@tanstack/react-query";
import { UserSearch } from "lucide-react";
import { useId } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import type {
  ContentFilterCorrelatedConversation,
  ContentFilterCorrelationResponse,
  ContentFilterEventItem,
} from "@/api";
import { Text } from "@/components/Text";
import { formatDateTime } from "@/i18n/format";
import { reviewConversationPath, useEventLabels } from "./contentSafety";
import { correlationOptions } from "./useReveal";

// Long names, emails and titles wrap in the narrow column: anywhere on phones, only at word ends on wider screens.
const WRAP = "max-w-full wrap-anywhere md:wrap-break-word";

function Conversation({
  eventUuid,
  conversation,
}: Readonly<{ eventUuid: string; conversation: ContentFilterCorrelatedConversation | null }>) {
  const { t } = useTranslation("adminContentSafety");
  if (!conversation) {
    return (
      <Text as="span" variant="caption" tone="tertiary">
        {t("revealed.noConversation")}
      </Text>
    );
  }
  // A deleted conversation comes without its title and no longer opens; group chat has no page in web/.
  // Admins can't open someone else's chat (MHS-1070): the link goes to the audited, read-only review instead.
  const title = conversation.deleted ? t("revealed.deleted") : conversation.title || t("revealed.untitled");
  if (conversation.kind === "chat" && !conversation.deleted) {
    return (
      <Link
        to={reviewConversationPath(eventUuid)}
        className={`text-content-primary underline underline-offset-2 ${WRAP}`}
      >
        <Text as="span" variant="caption">
          {title}
        </Text>
      </Link>
    );
  }
  return (
    <Text as="span" variant="caption" tone="secondary" className={WRAP}>
      {conversation.kind === "group_chat" ? t("revealed.groupChat", { title }) : title}
    </Text>
  );
}

/** Wraps after the @ rather than mid-word in the narrow User column. */
function Email({ email }: Readonly<{ email: string }>) {
  const at = email.indexOf("@") + 1;
  return (
    <Text as="span" variant="caption" tone="tertiary" className={WRAP}>
      {email.slice(0, at)}
      <wbr />
      {email.slice(at)}
    </Text>
  );
}

/** Who triggered the event and in which conversation, once revealed. */
function RevealedUser({ correlation }: Readonly<{ correlation: ContentFilterCorrelationResponse }>) {
  const { t } = useTranslation("adminContentSafety");
  const { user } = correlation;
  return (
    <div className="flex min-w-0 flex-col items-end gap-0.5 text-right">
      {/* Group chat usage is recorded against the room's owner. */}
      {correlation.feature === "group_chat" && (
        <Text as="span" variant="caption" tone="tertiary">
          {t("revealed.roomOwner")}
        </Text>
      )}
      {user ? (
        <>
          <Text as="span" variant="body-sm" className={WRAP}>
            {user.name}
          </Text>
          <Email email={user.email} />
        </>
      ) : (
        <Text as="span" variant="caption" tone="tertiary">
          {t("revealed.noUser")}
        </Text>
      )}
      <Conversation eventUuid={correlation.eventUuid} conversation={correlation.conversation} />
    </div>
  );
}

/** The event's User cell: Reveal until someone confirms a reveal on this page, then who it was. */
export function RevealCell({
  event,
  onReveal,
}: Readonly<{ event: ContentFilterEventItem; onReveal: (event: ContentFilterEventItem) => void }>) {
  const { t } = useTranslation("adminContentSafety");
  const labels = useEventLabels();
  const reasonId = useId();
  // Reads what a confirmed reveal stored; `enabled: false` so a row never requests (and audits) by itself.
  const revealed = useQuery({ ...correlationOptions(event.eventUuid), enabled: false });
  if (revealed.data) return <RevealedUser correlation={revealed.data.data} />;

  const button = (
    <Button
      size="sm"
      variant="secondary"
      leadingIcon={<UserSearch size={14} aria-hidden />}
      disabled={!event.correlatable}
      aria-describedby={event.correlatable ? undefined : reasonId}
      aria-label={t("reveal.labelFor", {
        category: labels.category(event.category),
        time: formatDateTime(event.createdAt),
      })}
      onClick={() => onReveal(event)}
    >
      {t("reveal.action")}
    </Button>
  );
  if (event.correlatable) return button;
  // A disabled button can't take focus, so the reason is also in its description for screen readers.
  return (
    <>
      <Tooltip content={t("reveal.unlinked")}>{button}</Tooltip>
      <span id={reasonId} className="sr-only">
        {t("reveal.unlinked")}
      </span>
    </>
  );
}
